/**
 * GlobalSidebars — Desktop-da sağ/sol fixed sidebar reklamlar.
 * Viewport >= 1480px olduqda görünür (content ilə üst-üstə düşməsin deyə).
 * Mobil/tablet-də render edilmir — bu reklamlar MobileBottomAds-da göstərilir.
 */
import { useEffect, useState, useRef } from 'react'
import api from '../api/axios'
import { useAuth } from '../context/AuthContext'
import { safeUrl } from '../utils/safeUrl'

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1480px)').matches
  )
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1480px)')
    const handler = e => setIsDesktop(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return isDesktop
}

function AdLink({ url, adId, children, style }) {
  const handleClick = () => {
    api.post(`/ads/${adId}/click`).catch(() => {})
  }
  const href = safeUrl(url)
  if (!href) return <span style={style}>{children}</span>
  if (href.startsWith('/')) {
    return <a href={href} style={style} onClick={handleClick}>{children}</a>
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
       style={style} onClick={handleClick}>
      {children}
    </a>
  )
}

function SidebarBox({ placement }) {
  const { user }      = useAuth()
  const [ad, setAd]   = useState(null)
  const ref           = useRef(null)
  const viewed        = useRef(false)
  const timerRef      = useRef(null)

  useEffect(() => {
    if (user?.vip) { setAd(null); return }
    const fetchAd = () => {
      api.get('/ads', { params: { placement } })
        .then(r => { setAd(r.data); viewed.current = false })
        .catch(() => setAd(null))
    }
    fetchAd()
    const iv = setInterval(fetchAd, 60_000)
    return () => clearInterval(iv)
  }, [placement, user?.vip])

  useEffect(() => {
    if (!ad || !ref.current) return
    viewed.current = false

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          timerRef.current = setTimeout(() => {
            if (!viewed.current) {
              viewed.current = true
              api.post(`/ads/${ad.id}/view`).catch(() => {})
            }
          }, 1000)
        } else {
          clearTimeout(timerRef.current)
        }
      },
      { threshold: 0.5 }
    )

    observer.observe(ref.current)
    return () => {
      observer.disconnect()
      clearTimeout(timerRef.current)
    }
  }, [ad?.id])

  if (!ad || user?.vip) return null

  return (
    <AdLink url={ad.targetUrl} adId={ad.id} style={{ textDecoration: 'none', display: 'block', width: 160 }}>
      <div
        ref={ref}
        style={{
          borderRadius: 'var(--radius)', overflow: 'hidden', position: 'relative',
          border: '1px solid rgba(var(--accent-rgb),.15)',
          background: 'rgba(var(--accent-rgb),.02)',
          transition: 'border-color .2s',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(var(--accent-rgb),.35)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(var(--accent-rgb),.15)'}
      >
        <div style={{
          position: 'absolute', top: 6, right: 6, zIndex: 2,
          fontSize: '.55rem', fontWeight: 700, letterSpacing: '.08em',
          padding: '.12rem .4rem', borderRadius: '100px',
          background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)',
          border: '1px solid rgba(var(--accent-rgb),.2)', color: 'var(--accent)',
          textTransform: 'uppercase',
        }}>Reklam</div>

        <img loading="lazy"
          src={ad.imageDesktop || ad.imageMobile}
          alt={ad.title}
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />

        <div style={{ padding: '.6rem .75rem' }}>
          <div style={{
            fontWeight: 700, fontSize: '.72rem', marginBottom: '.15rem',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {ad.title}
          </div>
          <div style={{
            fontSize: '.72rem', color: 'var(--muted)', lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {ad.description}
          </div>
        </div>
      </div>
    </AdLink>
  )
}

export default function GlobalSidebars() {
  const isDesktop = useIsDesktop()
  if (!isDesktop) return null

  return (
    <>
      <div style={{ position: 'fixed', right: 12, top: 80, zIndex: 50 }}>
        <SidebarBox placement="SIDEBAR_RIGHT" />
      </div>
      <div style={{ position: 'fixed', left: 12, top: 80, zIndex: 50 }}>
        <SidebarBox placement="SIDEBAR_LEFT" />
      </div>
    </>
  )
}
